import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, GitBranch, Calendar, MessageSquare, Store, IdCard,
  Sparkles, Award, MessageCircle, Settings, LogOut, Bell, Menu, X,
  CreditCard, FileText, BookOpen, UserCheck, Building2, BarChart3, Map as MapIcon, Mic,
  Inbox, MessageSquareWarning, Wand2, ScrollText, Images,
} from "lucide-react";

const ADMIN_NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/admin?tab=members", label: "Members", icon: Users },
  { to: "/admin?tab=pending", label: "Pending", icon: UserCheck },
  { to: "/admin?tab=payments", label: "Payments", icon: CreditCard },
  { to: "/admin?tab=events", label: "Events", icon: Calendar },
  { to: "/admin?tab=courses", label: "Courses", icon: BookOpen },
  { to: "/admin?tab=messages", label: "Messages", icon: MessageCircle },
  { to: "/admin/gallery", label: "Gallery", icon: Images },
  { to: "/admin/chapters", label: "Chapters", icon: Building2 },
  { to: "/feedback", label: "Feedback", icon: Inbox },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/members-map", label: "Map", icon: MapIcon },
];

const MEMBER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, exact: true },
  { to: "/dashboard?tab=profile", label: "Profile", icon: IdCard },
  { to: "/dashboard?tab=events", label: "Events", icon: Calendar },
  { to: "/referrals", label: "Referrals", icon: GitBranch },
  { to: "/pitch-ask", label: "Pitch & Ask", icon: Mic },
  { to: "/create", label: "Design Studio", icon: Wand2 },
  { to: "/contracts", label: "Contracts", icon: ScrollText },
  { to: "/feedback", label: "Feedback", icon: MessageSquareWarning },
  { to: "/directory", label: "Directory", icon: Users },
  { to: "/documents", label: "Documents", icon: FileText },
  { to: "/members-map", label: "Map", icon: MapIcon },
  { to: "/learning", label: "Learning", icon: BookOpen },
  { to: "/chapters", label: "Chapters", icon: Building2 },
];

export default function DashboardShell({ children, title, subtitle, actions, role = "member" }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  const items = role === "admin" ? ADMIN_NAV : MEMBER_NAV;
  const currentPath = loc.pathname + loc.search;

  const handleLogout = async () => { await logout(); nav("/"); };

  return (
    <div className="min-h-screen bg-[#FAF8F5] flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 left-0 h-screen w-60 bg-white border-r border-aubergine/[0.06] z-40 flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="px-5 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center min-w-0">
            <img src="/wowshe-logo.png" alt="WOWSHE" className="h-12 w-auto max-w-full object-contain" />
          </Link>
          <button onClick={() => setOpen(false)} className="lg:hidden text-plumSoft hover:text-aubergine"><X className="w-4 h-4" /></button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-2 px-3 space-y-0.5">
          {items.map((it) => {
            const Icon = it.icon;
            const isActive = it.exact ? currentPath === it.to : currentPath.startsWith(it.to);
            return (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`sidebar-${it.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`group flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] transition-colors relative ${
                  isActive
                    ? "bg-aubergine/[0.06] text-aubergine font-medium"
                    : "text-plumSoft/90 hover:bg-aubergine/[0.04] hover:text-aubergine"
                }`}
              >
                {isActive && <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r-full bg-aubergine" />}
                <Icon className={`w-[15px] h-[15px] flex-none ${isActive ? "text-aubergine" : "text-plumSoft/70 group-hover:text-aubergine"}`} />
                <span>{it.label}</span>
              </Link>
            );
          })}

          {role === "admin" && (
            <>
              <p className="text-[10px] uppercase tracking-[0.15em] text-plumSoft/50 mt-5 mb-1.5 px-3">Admin</p>
              <Link to="/admin?tab=settings" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-[13px] text-plumSoft/90 hover:bg-aubergine/[0.04] hover:text-aubergine">
                <Settings className="w-[15px] h-[15px] text-plumSoft/70" /><span>Settings</span>
              </Link>
            </>
          )}
        </nav>

        {/* Profile + Logout */}
        <div className="border-t border-aubergine/[0.06] p-3">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-aubergine/[0.08] text-aubergine flex items-center justify-center text-sm font-medium">
              {user?.name?.[0] || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[13px] font-medium text-aubergine truncate">{user?.name || "—"}</p>
              <p className="text-[10px] text-plumSoft/70 truncate">{user?.role === "admin" ? "Super Admin" : (user?.membership_status === "annual" ? "Annual Member" : "Member")}</p>
            </div>
            <button onClick={handleLogout} data-testid="sidebar-logout" className="text-plumSoft/70 hover:text-rose" aria-label="Sign out">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Backdrop */}
      {open && <div onClick={() => setOpen(false)} className="fixed inset-0 bg-aubergine/40 z-30 lg:hidden" />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-[#FAF8F5]/85 backdrop-blur-md border-b border-aubergine/[0.06]">
          <div className="px-6 lg:px-10 py-3.5 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button onClick={() => setOpen(true)} className="lg:hidden text-plumSoft"><Menu className="w-5 h-5" /></button>
              <div className="min-w-0">
                <h1 className="text-[17px] font-medium text-aubergine truncate tracking-tight" style={{ fontFamily: '"Outfit", sans-serif' }}>{title}</h1>
                {subtitle && <p className="text-xs text-plumSoft/70 mt-0.5 truncate">{subtitle}</p>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button className="relative w-9 h-9 rounded-lg text-plumSoft/80 hover:bg-aubergine/[0.06] hover:text-aubergine transition-colors flex items-center justify-center">
                <Bell className="w-[15px] h-[15px]" />
                <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-rose" />
              </button>
              {actions}
            </div>
          </div>
        </header>

        <div className="px-6 lg:px-10 py-7">{children}</div>
      </div>
    </div>
  );
}

// Minimal stat card — neutral chrome, accent only on number
export function ColorStat({ label, value, icon: Icon, tone = "purple" }) {
  const accents = {
    orange: "text-orange-500",
    teal:   "text-emerald-500",
    purple: "text-aubergine",
    blue:   "text-sky-500",
    rose:   "text-rose",
    gold:   "text-amber-500",
  };
  const accent = accents[tone] || accents.purple;
  return (
    <div className="bg-white rounded-xl border border-aubergine/[0.06] p-5 flex items-start justify-between gap-3 hover:border-aubergine/15 transition-colors">
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.12em] text-plumSoft/70">{label}</p>
        <p className={`text-2xl font-medium mt-2 truncate ${accent}`} style={{ fontFamily: '"Outfit", sans-serif' }}>{value}</p>
      </div>
      <div className={`w-9 h-9 rounded-lg bg-aubergine/[0.04] flex items-center justify-center shrink-0 ${accent}`}>
        <Icon className="w-[15px] h-[15px]" />
      </div>
    </div>
  );
}

export { Users, Calendar, BarChart3, Building2, GitBranch, UserCheck, Award, Sparkles, MessageSquare, Store };
